import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, ArrowRight, Loader2, Printer, RefreshCw, Snowflake, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { usePermission } from '@/lib/permissions'
import {
  useCancelTransfer,
  usePlanReturn,
  useReadyTransfer,
  useRevalidateTransfer,
  useStorageTransfer,
  useUpdateTransferMove,
} from '@/hooks/useStorageTransfers'
import { MovePreviewTable } from '@/components/equipment/storage/replace/MovePreviewTable'
import { TransferPlanQr } from '@/components/equipment/storage/replace/TransferPlanQr'
import { TransferMoveQrDialog } from '@/components/equipment/storage/replace/TransferMoveQrDialog'
import { CancelTransferDialog } from '@/components/equipment/storage/replace/CancelTransferDialog'
import { useAuth } from '@/providers/AuthProvider'
import { TransferStatusChip } from '@/components/equipment/storage/replace/TransferStatusChip'
import {
  approvalBlockers,
  dispositionStatement,
  summariseMoves,
} from '@/components/equipment/storage/replace/movePreview'
import { transferPrintHref } from '@/lib/transferPrint'
import type { RevalidationDiff, StorageTransferMove } from '@/types/storage'

/**
 * One transfer plan: what is moving, where it stands, and the two actions that
 * only make sense here — revalidating it against the freezer as it is now, and
 * planning the leg that brings the stock home.
 */
export function StorageTransferPage() {
  const { transferId } = useParams<{ transferId: string }>()
  const { can } = usePermission()
  const transfer = useStorageTransfer(transferId ?? null)
  const id = transferId ?? ''

  const ready = useReadyTransfer()
  const revalidate = useRevalidateTransfer(id)
  const planReturn = usePlanReturn(id)
  const cancel = useCancelTransfer(id)
  // A reopened DRAFT is settled here too (z8nrz7f2gh): pick or change a
  // destination, or exclude a row, without going back through the wizard.
  const updateMove = useUpdateTransferMove(id)

  const [drift, setDrift] = useState<RevalidationDiff | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [qrMove, setQrMove] = useState<StorageTransferMove | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const { user } = useAuth()

  if (transfer.isLoading) {
    return (
      <p className='p-8 text-center text-sm text-muted-foreground'>
        <Loader2 className='mr-2 inline h-4 w-4 animate-spin' />
        Loading the plan…
      </p>
    )
  }

  if (!transfer.data) {
    return <p className='p-8 text-center text-sm text-muted-foreground'>Plan not found.</p>
  }

  const t = transfer.data
  // Both codes now travel on the payload. This used to fetch every storage
  // root and match ids, which a list of plans could not have done at all.
  const sourceCode = t.sourceRoot?.code ?? t.moves[0]?.sourceNodePath.split('/')[0] ?? 'This unit'
  const targetCode = t.targetRoot?.code ?? t.moves[0]?.targetNodePath?.split('/')[0] ?? '—'
  const summary = summariseMoves(t.moves)
  const blockers = approvalBlockers(t.moves)
  const editable = t.status === 'DRAFT'
  const mayPlanReturn =
    t.status === 'COMPLETED' && t.disposition === 'RETURN_TO_SERVICE' && !t.returnOfTransferId
  const busy = ready.isPending || revalidate.isPending || planReturn.isPending || cancel.isPending

  const run = (p: Promise<unknown>) => {
    setError(null)
    void p.catch((e: unknown) =>
      setError(e instanceof Error ? e.message : 'That did not work'),
    )
  }

  return (
    <div className='space-y-3 p-4 md:p-6'>
      <Link
        to='/inventory/storage-transfers'
        className='mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground'
      >
        <ArrowLeft className='h-4 w-4' />
        All transfer plans
      </Link>

      <div className='mb-4 flex flex-wrap items-start justify-between gap-3'>
        <div>
          {/* The two units, in the heading. "Transfer plan" alone left the
              first question — which freezer is going where — answerable only
              by reading the move table. */}
          <h1 className='flex flex-wrap items-center gap-2 text-xl font-semibold'>
            <Snowflake className='h-5 w-5 text-sky-600' />
            {sourceCode}
            <ArrowRight className='h-4 w-4 text-muted-foreground' />
            {targetCode}
            <TransferStatusChip status={t.status} />
          </h1>
          <p className='mt-0.5 text-sm text-muted-foreground'>
            {t.reason?.label ?? t.reasonCode}
            {t.reasonNote && ` — ${t.reasonNote}`}
          </p>
        </div>

        <div className='flex flex-wrap gap-2'>
          {/* Read-only, so offered to anyone who can see this page at all —
              no narrower than the view permission the route already requires. */}
          <Button variant='outline' asChild>
            <a href={transferPrintHref(id)} target='_blank' rel='noreferrer'>
              <Printer className='mr-1.5 h-4 w-4' />
              Print
            </a>
          </Button>
          {can('storage.replace') && (t.status === 'DRAFT' || t.status === 'READY') && (
            <Button
              variant='outline'
              disabled={busy}
              onClick={() =>
                run(revalidate.mutateAsync().then((d) => setDrift(d)))
              }
            >
              <RefreshCw className='mr-1.5 h-4 w-4' />
              Revalidate
            </Button>
          )}
          {can('storage.replace') && editable && blockers.length === 0 && (
            <Button disabled={busy} onClick={() => run(ready.mutateAsync(id))}>
              Approve plan
            </Button>
          )}
          {can('storage.replace') && mayPlanReturn && (
            <Button disabled={busy} onClick={() => run(planReturn.mutateAsync())}>
              <ArrowRight className='mr-1.5 h-4 w-4' />
              Plan return
            </Button>
          )}
          {can('storage.replace') && t.status !== 'CANCELLED' && t.status !== 'COMPLETED' && (
            <Button
              variant='outline'
              disabled={busy}
              // A signed act with a reason — cancelling is what unfreezes the
              // source, so the trail has to say who lifted it and why.
              onClick={() => setCancelling(true)}
            >
              <X className='mr-1.5 h-4 w-4' />
              Cancel plan
            </Button>
          )}
        </div>
      </div>

      {/* The counterpart leg, whichever side you arrived from. */}
      {t.returnOfTransferId && (
        <p className='mb-3 text-sm'>
          This is the return leg of{' '}
          <Link className='underline' to={`/inventory/storage-transfers/${t.returnOfTransferId}`}>
            the outbound transfer
          </Link>
          .
        </p>
      )}

      <Card className='mb-4'>
        <CardHeader className='pb-2'>
          <CardTitle className='text-base'>What happens to the source</CardTitle>
        </CardHeader>
        <CardContent className='space-y-1 text-sm'>
          <p className='text-muted-foreground'>
            {dispositionStatement(t.disposition, sourceCode)}
          </p>
          <p>
            {summary.total} occupant{summary.total === 1 ? '' : 's'} · {summary.planned} planned
            {summary.excluded > 0 && ` · ${summary.excluded} excluded`}
            {summary.unmapped > 0 && ` · ${summary.unmapped} with no destination`}
            {summary.conditionFailures > 0 && ` · ${summary.conditionFailures} unsuitable`}
          </p>
          {/* Carried out from the phone (z8nrz7e7ak). A skip at the freezer has
              a scan time; an exclusion made while drafting does not. */}
          {(t.status === 'IN_PROGRESS' || t.status === 'COMPLETED') && (
            <p>
              Carried out: {t.moves.filter((m) => m.status === 'MOVED').length} moved ·{' '}
              {t.moves.filter((m) => m.status === 'SKIPPED' && m.scannedAt).length} skipped ·{' '}
              {t.moves.filter((m) => m.status === 'PENDING').length} to go
            </p>
          )}
        </CardContent>
      </Card>

      {/* Only once approved. A draft is still being edited, so a code pointing
          at one invites somebody to walk to a freezer with a plan that may
          change before they get there. */}
      {(t.status === 'READY' || t.status === 'IN_PROGRESS') && (
        <div className='mb-4'>
          <TransferPlanQr transferId={t.id} />
        </div>
      )}

      {drift && (
        <div
          className={cn(
            'mb-4 rounded-md border p-3 text-sm',
            drift.goneOccupants.length + drift.newOccupants + drift.takenDestinations.length === 0
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-amber-300 bg-amber-50 text-amber-900',
          )}
        >
          <p className='font-medium'>
            {drift.applied ? 'Plan updated against the freezer' : 'Drift since this was approved'}
          </p>
          <p className='text-[13px]'>
            {drift.goneOccupants.length} occupant(s) have left, {drift.newOccupants} arrived,{' '}
            {drift.takenDestinations.length} destination(s) are no longer free.
            {/* Reported, not applied, once approved: rewriting a plan under its
                assignee is how a tech scans a slot nobody approved. */}
            {!drift.applied &&
              ' An approved plan is not rewritten automatically — cancel and re-plan if this matters.'}
          </p>
        </div>
      )}

      {blockers.length > 0 && editable && (
        <div className='mb-4 space-y-1 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900'>
          <p className='flex items-center gap-1.5 font-medium'>
            <AlertTriangle className='h-4 w-4' />
            Not ready to approve
          </p>
          {blockers.map((b) => (
            <p key={b.kind} className='text-[13px]'>
              {b.message}
            </p>
          ))}
        </div>
      )}

      {error && (
        <p className='mb-4 rounded-md border border-red-200 bg-red-50 p-2 text-[13px] text-red-800'>
          {error}
        </p>
      )}

      {/* The moves themselves: every occupant, where it is now and where it
          is planned to go, grouped by the box it comes out of. */}
      <MovePreviewTable
        moves={t.moves}
        actions={
          editable && can('storage.replace')
            ? {
                targetUnitId: t.targetRootId,
                busyOrdinal: updateMove.isPending
                  ? (t.moves.find((m) => m.id === updateMove.variables?.moveId)?.ordinal ?? null)
                  : null,
                onRetarget: (m, ref) => {
                  const row = t.moves.find((r) => r.ordinal === m.ordinal)
                  if (!row) return
                  setError(null)
                  updateMove.mutate(
                    { moveId: row.id, targetNodeRef: ref },
                    { onError: (e) => setError(e.message) },
                  )
                },
                onExclude: (m, reason) => {
                  const row = t.moves.find((r) => r.ordinal === m.ordinal)
                  if (!row) return
                  setError(null)
                  updateMove.mutate(
                    { moveId: row.id, exclude: true, skipReason: reason },
                    { onError: (e) => setError(e.message) },
                  )
                },
              }
            : undefined
        }
        // Per-move codes on the same terms as the plan's: approved plans only.
        onShowQr={
          t.status === 'READY' || t.status === 'IN_PROGRESS'
            ? (m) => setQrMove(t.moves.find((row) => row.ordinal === m.ordinal) ?? null)
            : undefined
        }
      />
      <TransferMoveQrDialog
        transferId={t.id}
        move={qrMove}
        sourceCode={sourceCode}
        targetCode={targetCode}
        onClose={() => setQrMove(null)}
      />
      <CancelTransferDialog
        open={cancelling}
        onOpenChange={setCancelling}
        sourceCode={sourceCode}
        signerName={user?.fullName || user?.email}
        onCancel={(reason, signingToken) => cancel.mutateAsync({ reason, signingToken })}
      />
    </div>
  )
}
